import type { Item } from '@closetly/core';
import { pb } from './pb';

const MAX_SIDE = 1600;

/**
 * Scales a camera photo down before upload: a 12 MP picture is several MB,
 * the card shows 72 px and the sheet a few hundred. Falls back to the
 * original file if the browser can't decode it.
 */
export async function shrinkPhoto(file: File): Promise<File> {
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();

    for (const type of ['image/webp', 'image/jpeg']) {
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, 0.85));
      // Safari silently returns PNG for unsupported types
      if (blob && blob.type === type) {
        const ext = type === 'image/webp' ? 'webp' : 'jpg';
        return new File([blob], `foto.${ext}`, { type });
      }
    }
  } catch {
    // keep the original
  }
  return file;
}

/** URL of the picture stored with the item; cards never load from the shop's CDN. */
export function storedImageUrl(item: Pick<Item, 'id' | 'image'>, thumb = '400x0'): string {
  return item.image ? pb.files.getURL({ ...item, collectionName: 'items' }, item.image, { thumb }) : '';
}
